/**
 * DB-content localization. Each content row carries an optional `i18n` jsonb
 * column shaped `{ <lang>: { <field>: <translated value> } }`. At read time we
 * overlay the active language's translations onto the English base columns,
 * field-by-field, falling back to English whenever a translation is missing or
 * empty. English (`en`) always uses the base columns — there are no `en` keys.
 *
 * Usage in a public read:
 *   const { i18n } = useTranslation()
 *   setTours(localizeRows(data, i18n.language, I18N_FIELDS.tours))
 */

type I18nRow = { i18n?: Record<string, Record<string, unknown>> | null }

/** Translatable fields per content entity (the English columns these override). */
export const I18N_FIELDS = {
  tours: [
    'title',
    'description',
    'location',
    'meeting_point',
    'features',
    'requirements',
    'included_items',
    'optional_extras',
  ],
  activities: [
    'title',
    'description',
    'location',
    'meeting_point',
    'features',
    'requirements',
    'whats_included',
  ],
  guides: ['bio'],
  partners: ['description'],
  self_guided_routes: ['title', 'description'],
} as const

/** Overlay one row's translations for `lang` onto its English base fields. */
export function localizeRow<T extends I18nRow>(
  row: T,
  lang: string,
  fields: readonly string[]
): T {
  const tr = row?.i18n?.[lang]
  if (!tr || lang === 'en') return row
  const out = { ...row } as Record<string, unknown>
  for (const f of fields) {
    const v = tr[f]
    if (v != null && v !== '') out[f] = v
  }
  return out as T
}

/** Localize an array of rows. No-op (returns the same shape) for English. */
export function localizeRows<T extends I18nRow>(
  rows: T[],
  lang: string,
  fields: readonly string[]
): T[] {
  if (lang === 'en' || !Array.isArray(rows)) return rows
  return rows.map((r) => localizeRow(r, lang, fields))
}
